
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Vote, Clock, Users, CheckCircle, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";

type Poll = {
  id: string;
  service_id: string;
  target_user_id: string | null;
  poll_type: 'service_deletion' | 'user_ban';
  title: string;
  description: string | null;
  created_at: string;
  expires_at: string;
  is_active: boolean;
  services?: {
    title: string;
    user_id: string;
  } | null;
  target_profiles?: {
    first_name: string | null;
    last_name: string | null;
  } | null;
  user_votes?: {
    id: string;
    vote: 'yes' | 'no';
  }[];
  _count?: {
    yes_votes: number;
    no_votes: number;
    total_votes: number;
  };
};

const Polls = () => {
  const { user } = useAuth();
  const [polls, setPolls] = useState<Poll[]>([]);
  const [loading, setLoading] = useState(true);
  const [votingLoading, setVotingLoading] = useState<string | null>(null);

  const fetchPolls = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Fetch active polls that expire within 7 days
      const sevenDaysFromNow = new Date();
      sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
      
      const { data: pollsData, error } = await supabase
        .from('service_deletion_polls')
        .select(`
          *,
          services:service_id (
            title,
            user_id
          )
        `)
        .eq('is_active', true)
        .lte('expires_at', sevenDaysFromNow.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!pollsData) {
        setPolls([]);
        return;
      }

      // Fetch additional data for each poll
      const pollsWithVotes = await Promise.all(
        pollsData.map(async (poll) => {
          // Get anonymous vote counts using secure function
          const { data: pollResults, error: resultsError } = await supabase
            .rpc('get_poll_results', { poll_id_param: poll.id });

          if (resultsError) {
            console.error('Error fetching poll results:', resultsError);
          }

          // Get user's vote status using secure function
          const { data: hasVoted, error: hasVotedError } = await supabase
            .rpc('has_user_voted', { poll_id_param: poll.id });

          if (hasVotedError) {
            console.error('Error checking if user voted:', hasVotedError);
          }

          // Get user's actual vote if they voted
          let userVote = null;
          if (hasVoted) {
            const { data: vote, error: voteError } = await supabase
              .rpc('get_user_vote', { poll_id_param: poll.id });
            
            if (!voteError) {
              userVote = { vote };
            }
          }

          // Transform results into the expected format
          const votes = pollResults || [];
          const voteCountYes = votes.find(r => r.vote_option === 'yes')?.vote_count || 0;
          const voteCountNo = votes.find(r => r.vote_option === 'no')?.vote_count || 0;

          // Get target user profile if this is a user ban poll
          let targetProfile = null;
          if (poll.poll_type === 'user_ban' && poll.target_user_id) {
            const { data: profileData, error: profileError } = await supabase
              .from('profiles')
              .select('first_name, last_name')
              .eq('id', poll.target_user_id)
              .maybeSingle();

            if (profileError) {
              console.error('Error fetching target profile:', profileError);
            } else {
              targetProfile = profileData;
            }
          }

          return {
            ...poll,
            poll_type: poll.poll_type as 'service_deletion' | 'user_ban',
            target_profiles: targetProfile,
            user_votes: userVote ? [userVote as { id: string; vote: 'yes' | 'no' }] : [],
            _count: {
              yes_votes: voteCountYes,
              no_votes: voteCountNo,
              total_votes: voteCountYes + voteCountNo
            }
          };
        })
      );

      setPolls(pollsWithVotes);

    } catch (error: any) {
      console.error('Error fetching polls:', error);
      toast.error('Failed to load polls');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (pollId: string, vote: 'yes' | 'no') => {
    if (!user) return;

    try {
      setVotingLoading(pollId);

      // Check if user already voted using secure function
      const { data: hasVoted, error: hasVotedError } = await supabase
        .rpc('has_user_voted', { poll_id_param: pollId });

      if (hasVotedError) {
        console.error('Error checking vote status:', hasVotedError);
        toast.error('Error checking vote status');
        return;
      }

      if (hasVoted) {
        toast.error('You have already voted on this poll');
        return;
      }

      // Cast vote
      const { error } = await supabase
        .from('poll_votes')
        .insert({
          poll_id: pollId,
          user_id: user.id,
          vote: vote
        });

      if (error) {
        // Handle unique constraint violation gracefully
        if (error.code === '23505') {
          toast.error('You have already voted on this poll');
        } else {
          throw error;
        }
        return;
      }

      toast.success('Vote cast successfully!');
      await fetchPolls(); // Refresh polls to show updated counts

    } catch (error: any) {
      console.error('Error casting vote:', error);
      toast.error('Failed to cast vote');
    } finally {
      setVotingLoading(null);
    }
  };

  useEffect(() => {
    if (user) {
      fetchPolls();
    }
  }, [user]);

  if (!user) {
    return <Navigate to="/auth?mode=login" replace />;
  }

  const getTimeRemaining = (expiresAt: string) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires.getTime() - now.getTime();
    
    if (diff <= 0) return 'Expired';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h remaining`;
    return `${hours}h remaining`;
  };

  const getTargetName = (poll: Poll) => {
    if (poll.poll_type === 'service_deletion' && poll.services) {
      return poll.services.title;
    }
    if (poll.poll_type === 'user_ban' && poll.target_profiles) {
      const firstName = poll.target_profiles.first_name || '';
      const lastName = poll.target_profiles.last_name || '';
      return `${firstName} ${lastName}`.trim() || 'Unknown User';
    }
    return 'Unknown';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 space-y-8">
        <div className="text-center mb-8">
          <h1 className="font-poppins font-bold text-4xl mb-4 flex items-center justify-center gap-3">
            <Vote className="h-10 w-10 text-anyskill-purple" />
            Community Polls
          </h1>
          <p className="font-inter text-xl text-gray-600 mb-4">
            Participate in community decisions on service quality and user conduct
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl mx-auto">
            <div className="flex items-center gap-2 text-blue-700 font-medium text-sm">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Anonymous Voting Protected
            </div>
            <p className="text-blue-600 text-sm mt-1">
              Your votes are private and anonymous. Only aggregate results are visible to the community.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-anyskill-purple" />
          </div>
        ) : polls.length === 0 ? (
          <div className="text-center py-8">
            <Vote className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-inter text-lg mb-2">
              No active polls at the moment
            </p>
            <p className="text-gray-400 font-inter">
              Check back later for community decisions
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {polls.map((poll) => {
              const hasVoted = poll.user_votes && poll.user_votes.length > 0;
              const userVote = hasVoted ? poll.user_votes[0].vote : null;
              const timeRemaining = getTimeRemaining(poll.expires_at);
              const isExpired = timeRemaining === 'Expired';

              return (
                <Card key={poll.id} className="border border-gray-200">
                  <CardHeader className="space-y-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg font-poppins line-clamp-2">
                        {poll.title}
                      </CardTitle>
                      <Badge 
                        variant={poll.poll_type === 'service_deletion' ? 'destructive' : 'secondary'}
                        className="text-xs shrink-0 ml-2"
                      >
                        {poll.poll_type === 'service_deletion' ? 'Service' : 'User Ban'}
                      </Badge>
                    </div>
                    
                    <div className="text-sm text-gray-600 font-inter">
                      Target: <span className="font-medium">{getTargetName(poll)}</span>
                    </div>
                    
                    {poll.description && (
                      <p className="text-sm text-gray-500 font-inter line-clamp-2">
                        {poll.description}
                      </p>
                    )}
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{timeRemaining}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        <span>{poll._count?.total_votes || 0} votes</span>
                      </div>
                    </div>

                    {poll._count && poll._count.total_votes > 0 && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="flex items-center gap-1">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            Yes: {poll._count.yes_votes}
                          </span>
                          <span className="flex items-center gap-1">
                            <XCircle className="h-4 w-4 text-red-600" />
                            No: {poll._count.no_votes}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full transition-all duration-300"
                            style={{ 
                              width: `${(poll._count.yes_votes / poll._count.total_votes) * 100}%` 
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {!isExpired && !hasVoted ? (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleVote(poll.id, 'yes')}
                          disabled={votingLoading === poll.id}
                          className="flex-1 text-green-600 border-green-600 hover:bg-green-50"
                        >
                          {votingLoading === poll.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Yes
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleVote(poll.id, 'no')}
                          disabled={votingLoading === poll.id}
                          className="flex-1 text-red-600 border-red-600 hover:bg-red-50"
                        >
                          {votingLoading === poll.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <XCircle className="h-4 w-4 mr-1" />
                              No
                            </>
                          )}
                        </Button>
                      </div>
                    ) : hasVoted ? (
                      <div className="text-center">
                        <Badge variant="secondary" className="text-sm">
                          You voted: {userVote?.toUpperCase()}
                        </Badge>
                      </div>
                    ) : (
                      <div className="text-center">
                        <Badge variant="outline" className="text-sm">
                          Poll Expired
                        </Badge>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Polls;
